// web-vite/src/components/Header.tsx
//
// Mirrors src/components/common/Header.tsx — same layout, badges, colors,
// font weights. Differences from mobile:
//   - No back button (web uses sidebar nav, not stack history)
//   - Battery / network are read from browser APIs (navigator.battery is
//     deprecated/limited; we just show a static "online" icon for now).
//   - Sync timestamp is per-screen, not global Redux. The header shows a
//     placeholder "now" until P3.11 introduces a real global sync slice.

import React, {useEffect, useState} from 'react'
import clsx from 'clsx'
import {useNavigate} from 'react-router-dom'
import {ArrowLeft, BatteryFull, LogOut, Moon, RefreshCw, Sun, Wifi, WifiOff} from 'lucide-react'
import {useAuth} from '../app/useAuth'

function isMorningShift(): boolean {
  const h = new Date().getHours()
  return h >= 6 && h < 18
}

function shiftRange(morning: boolean): string {
  return morning ? '06:00 - 18:00' : '18:00 - 06:00'
}

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface HeaderProps {
  /** When provided, replaces the date/time block (matches mobile back-button mode). */
  title?: string
  /** ISO timestamp of the last successful Supabase fetch. */
  lastSyncedAt?: string | null
  /**
   * When provided, renders a back button on the left.
   * - String value: navigate to that path.
   * - `true`: navigate(-1) — go back in history.
   */
  backTo?: string | true
}

function formatTimeAgo(iso: string | null | undefined): string {
  if (!iso) return 'just now'
  const elapsed = Date.now() - Date.parse(iso)
  if (elapsed < 60_000) return 'just now'
  if (elapsed < 3_600_000) return `${Math.floor(elapsed / 60_000)}m ago`
  if (elapsed < 86_400_000) return `${Math.floor(elapsed / 3_600_000)}h ago`
  return `${Math.floor(elapsed / 86_400_000)}d ago`
}

export const Header: React.FC<HeaderProps> = ({title, lastSyncedAt, backTo}) => {
  const navigate = useNavigate()
  const {user, profile, signOut} = useAuth()
  const onBack = () => {
    if (backTo === true) navigate(-1)
    else if (typeof backTo === 'string') navigate(backTo)
  }

  // Display name resolution, highest priority first:
  //   1. users.display_name      — admin-set label, e.g. "Alex J"
  //   2. linked personnel.name   — falls out of the FK join automatically
  //   3. email prefix            — defensive fallback before profile loads
  //   4. literal "Account"       — last resort
  const displayLabel = (() => {
    if (profile?.display_name) return profile.display_name
    if (profile?.personnel_name) return profile.personnel_name
    const email = user?.email ?? ''
    if (!email) return 'Account'
    const prefix = email.split('@')[0]
    return prefix.length > 16 ? prefix.slice(0, 14) + '…' : prefix
  })()
  // Tick the clock every minute so the displayed time stays current.
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(id)
  }, [])

  // Network status — navigator.onLine is the cheapest reliable signal.
  const [online, setOnline] = useState<boolean>(() => navigator.onLine)
  useEffect(() => {
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])

  const morning = isMorningShift()

  return (
    // shrink-0 + explicit height — without it, when the header's flex
    // descendant (a pill or badge) computes a taller intrinsic height,
    // the header silently expands beyond the 60px declared height.
    <header className="flex h-header shrink-0 items-center justify-between border-b border-divider bg-background px-xl">
      {/* Left — back button (when on a subroute) + title or date/time */}
      <div className="flex items-center gap-md leading-none">
        {backTo && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-surfaceBorder text-textPrimary hover:border-primary hover:text-primary"
          >
            <ArrowLeft size={18} />
          </button>
        )}
        {title ? (
          <h2 className="text-lg font-semibold text-textPrimary">{title}</h2>
        ) : (
          <div className="flex items-baseline gap-md">
            <span className="text-md font-semibold text-textPrimary">
              {formatDate(now)}
            </span>
            <span className="text-md text-textSecondary">{formatTime(now)}</span>
          </div>
        )}
      </div>

      {/* Right — shift, sync, network, user */}
      <div className="flex items-center gap-md">
        {/* Shift badge */}
        <div className="flex items-center gap-sm rounded-round bg-primaryBg px-md py-sm">
          {morning ? (
            <Sun size={14} color="var(--color-primary)" />
          ) : (
            <Moon size={14} color="var(--color-primary)" />
          )}
          <span className="text-sm font-semibold text-primary">
            {morning ? 'Morning' : 'Night'} Shift
          </span>
          <span className="text-xs text-textSecondary">
            {shiftRange(morning)}
          </span>
        </div>

        {/* Sync badge */}
        <div className="flex items-center gap-sm rounded-round bg-surface px-md py-sm">
          <RefreshCw size={14} color="var(--color-statusGreen)" />
          <span className="text-sm text-textSecondary">
            {formatTimeAgo(lastSyncedAt)}
          </span>
        </div>

        {/* Network + battery group. Browser doesn't expose battery API
            reliably anymore (deprecated in most browsers), so we show a
            static green BatteryFull glyph to match the mobile layout. */}
        <div className="flex items-center gap-sm">
          {online ? (
            <Wifi size={18} color="var(--color-statusGreen)" />
          ) : (
            <WifiOff size={18} color="var(--color-statusRed)" />
          )}
          <BatteryFull size={18} color="var(--color-statusGreen)" />
        </div>

        {/* User badge — clicking the LogOut icon signs the user out and
            triggers AuthGate to re-render the LoginScreen. */}
        <div
          className={clsx(
            'flex items-center gap-sm rounded-round border border-surfaceBorder',
            'bg-surface px-lg py-sm',
          )}
        >
          <span className="text-md font-medium text-textPrimary" title={user?.email ?? undefined}>
            {displayLabel}
          </span>
          <button
            type="button"
            onClick={async (e) => {
              e.preventDefault()
              e.stopPropagation()
              await signOut()
            }}
            aria-label="Sign out"
            title="Sign out"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-textSecondary hover:bg-surfaceLight hover:text-textPrimary"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </header>
  )
}
